import {isFeatureEnabled} from 'feature-flags';
import {Card} from 'platform/components';
import {Box, HStack, Link, Show, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {SourcingVehicleDetailResponseBody} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {DEFAULT_CURRENCY_CODE} from '../../../constants/DEFAULT_CURRENCY_CODE';
import {useSourcingCurrency} from '../../../hooks/useSourcingCurrency';
import {getClassifiedsAdInactiveReason} from '../../../utils/isClassifiedsAdInactive';
import {isUnifiedSourcingEnabled} from '../../../utils/isUnifiedSourcingEnabled';
import {BpmEstimateCard} from '../../BpmEstimate/BpmEstimateCard';
import {DescriptionCard} from '../../ListingPreview/components/DescriptionCard';
import {FeatureList} from '../../ListingPreview/components/FeatureList';
import {ListingGallery} from '../../ListingPreview/components/ListingGallery';
import {ListingTitle} from '../../ListingPreview/components/ListingTitle';
import {PriceCard} from '../../ListingPreview/components/PriceCard';
import {SellerCard} from '../../ListingPreview/components/SellerCard';
import {buildPriceBreakdown} from '../../ListingPreview/utils/buildPriceBreakdown';
import {buildSpecificationItems} from '../../ListingPreview/utils/buildSpecificationItems';
import {getPremiumFeatures} from '../../ListingPreview/utils/getPremiumFeatures';
import {toSafeExternalUrl} from '../../ListingPreview/utils/toSafeExternalUrl';
import {getFirstScrapedPrice} from '../../PriceChangesPopper/getFirstScrapedPrice';
import {PriceChangesDetail} from '../../PriceChangesPopper/PriceChangesDetail';
import {getSellerEmail} from '../utils/getSellerEmail';
import {getSellerMapUrl} from '../utils/getSellerMapUrl';
import {getSellerPhoneNumbers} from '../utils/getSellerPhoneNumbers';
import {AdServerLogo} from './AdServerLogo';

interface VehiclePreviewRedesignProps extends TestIdProps {
  detail: SourcingVehicleDetailResponseBody;
  adLink: string | Nullish;
  isBpmEstimateVisible: boolean;
}

/** Classified preview; content reflows with the resizable panel width. */
export function VehiclePreviewRedesign(props: VehiclePreviewRedesignProps) {
  const enhancedSurfaces = isUnifiedSourcingEnabled();
  const formatNumber = useFormatNumber();
  const currency = useSourcingCurrency();

  const sourcingVehicle = props.detail.sourcingVehicle;
  const price = sourcingVehicle.price;
  const seller = sourcingVehicle.seller;

  const specifications = buildSpecificationItems({
    year: isNotNil(sourcingVehicle.year) ? sourcingVehicle.year.toString() : null,
    power: isNotNil(sourcingVehicle.power)
      ? `${sourcingVehicle.power} ${i18n.t('general.metric.kW')}`
      : null,
    driveType: sourcingVehicle.driveType?.translation,
    mileage: isNotNil(sourcingVehicle.mileage)
      ? `${formatNumber(sourcingVehicle.mileage)} ${i18n.t('general.metric.km')}`
      : null,
    transmission: sourcingVehicle.transmission?.translation,
    fuelType: sourcingVehicle.fuelType?.translation,
    carStyle: sourcingVehicle.carStyle?.translation,
  });

  const priceBreakdown = buildPriceBreakdown({
    price: isNotNil(price) ? props.detail.summary.original : null,
    currency: currency?.code ?? DEFAULT_CURRENCY_CODE,
    originalPrice: price?.originalWithVat,
    originalCurrency: price?.originalCurrency?.key,
    vatRate: price?.vatRate,
    isVatDeductible: price?.vatReclaimable,
  });

  const sellerCountryName = seller?.country?.translation;
  const vatIncluded = price?.vatReclaimable
    ? i18n.t('entity.sourcing.vehiclePreview.labels.vatIncluded', {
        vat: formatNumber(price.vatRate, 2),
      })
    : null;
  // Same discount as the flag on the photo in the vehicle header.
  const convertedPrice = props.detail.summary.original;
  const firstScrapedPrice = getFirstScrapedPrice(sourcingVehicle, convertedPrice);
  const priceChange =
    isFeatureEnabled(featureFlags.SOURCING_PRICE_CHANGES) &&
    isNotNil(convertedPrice) &&
    isNotNil(firstScrapedPrice)
      ? firstScrapedPrice - convertedPrice
      : null;

  // A sold or vanished ad greys out the price card instead of a flag next to the title.
  const soldReason = match(getClassifiedsAdInactiveReason(sourcingVehicle))
    .with('availabilityCheck', () => i18n.t('entity.sourcing.labels.justSoldTooltip'))
    .with('inactiveListing', () => i18n.t('entity.sourcing.labels.inactiveAdvertisementTooltip'))
    .with(null, () => null)
    .exhaustive();

  const vatNote =
    isNotNil(vatIncluded) && isNotNilOrEmpty(sellerCountryName)
      ? `${vatIncluded} (${sellerCountryName})`
      : vatIncluded;

  const premiumFeatures = getPremiumFeatures(
    props.detail.features,
    sourcingVehicle.interiorMaterial
  );

  const features = sourcingVehicle.features
    .filter((feature) => feature.key !== feature.translation)
    .map((feature) => ({key: feature.key, label: feature.translation}));

  const header = (
    <HStack justify="space-between" align="center" spacing={2} wrap>
      {isNotNil(sourcingVehicle.server) ? (
        <AdServerLogo server={sourcingVehicle.server} maxHeight={6} maxWidth={32} />
      ) : (
        <Box />
      )}
      <Show when={toSafeExternalUrl(props.adLink)}>
        {(adLink) => (
          <Link
            title={i18n.t('entity.sourcing.vehiclePreview.actions.openOriginalUrl')}
            rightIcon="action/open_in_new"
            size="small"
            href={adLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={suffixTestId('openOriginalUrl', props)}
          />
        )}
      </Show>
    </HStack>
  );

  return (
    <VStack spacing={3}>
      <Show when={!enhancedSurfaces}>{header}</Show>
      <Card>
        <VStack spacing={4}>
          <Show when={enhancedSurfaces}>{header}</Show>
          <VStack>
            <ListingGallery
              key={sourcingVehicle.adId}
              pictureUrls={sourcingVehicle.pictureUrls}
              thumbnailUrl={sourcingVehicle.thumbnailUrl?.full}
              data-testid={suffixTestId('gallery', props)}
            />
          </VStack>
          <ListingTitle
            title={sourcingVehicle.adTitle}
            make={sourcingVehicle.make?.key}
            specifications={specifications}
            cebiaReportExternalId={sourcingVehicle.adId}
            data-testid={suffixTestId('title', props)}
          />
          {/* A sold ad without a price still shows the card, so the "Sold" badge is not lost. */}
          <Show when={isNotNil(priceBreakdown) || isNotNil(soldReason)}>
            <PriceCard
              label={i18n.t(
                price?.vatReclaimable
                  ? 'entity.sourcing.vehiclePreview.labels.priceWithVat'
                  : 'general.labels.price'
              )}
              breakdown={priceBreakdown}
              isVatDeductible={price?.vatReclaimable}
              vatNote={vatNote}
              priceChange={priceChange}
              soldReason={soldReason}
              // Shown only on the price change badge, which needs a converted price to exist.
              priceChangeDetail={
                <Show when={isNotNil(convertedPrice) ? convertedPrice : null}>
                  {(currentPrice) => (
                    <PriceChangesDetail
                      vehicle={sourcingVehicle}
                      currentPrice={currentPrice}
                      currency={currency?.code ?? DEFAULT_CURRENCY_CODE}
                      data-testid={suffixTestId('priceChanges', props)}
                    />
                  )}
                </Show>
              }
              data-testid={suffixTestId('price', props)}
            />
          </Show>
        </VStack>
      </Card>
      <Show when={props.isBpmEstimateVisible}>
        <Card>
          <BpmEstimateCard
            sourcingVehicle={sourcingVehicle}
            data-testid={suffixTestId('bpm', props)}
          />
        </Card>
      </Show>
      <Show when={seller}>
        {(vehicleSeller) => (
          <SellerCard
            title={i18n.t('entity.sourcing.vehiclePreview.labels.dealer')}
            name={vehicleSeller.name}
            countryCode={vehicleSeller.country?.key}
            address={[vehicleSeller.address, vehicleSeller.city].filter(isNotNilOrEmpty).join(', ')}
            mapUrl={getSellerMapUrl(vehicleSeller)}
            phoneNumbers={getSellerPhoneNumbers(vehicleSeller)}
            email={getSellerEmail(vehicleSeller)}
            data-testid={suffixTestId('seller', props)}
          />
        )}
      </Show>
      <FeatureList
        key={`features-${sourcingVehicle.adId}`}
        features={features}
        premiumFeatures={premiumFeatures}
        data-testid={suffixTestId('features', props)}
      />
      <DescriptionCard
        key={`description-${sourcingVehicle.adId}`}
        description={sourcingVehicle.description}
        data-testid={suffixTestId('description', props)}
      />
    </VStack>
  );
}
